/**
 * InterviewReadinessOutput — renders the interview_readiness step: an overall
 * recommendation chip, readiness-dimension score bars, priority focus areas,
 * top risks, a 30-day plan, and the mock-interview plan, with the narrative trail.
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, ScoreBar, SectionLabel } from "./shared";

export default function InterviewReadinessOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const rec: string | undefined = o.overall_recommendation;
  const dims: any[] = o.readiness_dimensions ?? [];
  const risks: any[] = o.top_risks ?? [];
  const plan: any[] = o.thirty_day_plan ?? [];

  return (
    <Stack spacing={2.5}>
      {rec && (
        <Box>
          <SectionLabel>{t("InterviewReadinessOutput.recommendation")}</SectionLabel>
          <Chip
            label={rec}
            sx={{ mt: 0.75, fontWeight: 500, bgcolor: alpha(theme.palette.primary.light, 0.12), color: theme.palette.primary.main }}
          />
        </Box>
      )}

      {dims.length > 0 && (
        <Box>
          <SectionLabel>{t("InterviewReadinessOutput.readinessByDimension")}</SectionLabel>
          <Stack spacing={1.25} mt={1}>
            {dims.map((d, i) => (
              <ScoreBar
                key={`${d.dimension}-${i}`}
                label={d.dimension ?? ""}
                value={Number(d.score) || 0}
              />
            ))}
          </Stack>
        </Box>
      )}

      <BulletSection label={t("InterviewReadinessOutput.priorityFocusAreas")} items={o.priority_focus_areas} />

      {risks.length > 0 && (
        <Box>
          <SectionLabel>{t("InterviewReadinessOutput.topRisks")}</SectionLabel>
          <Stack spacing={1.25} mt={0.75}>
            {risks.map((r, i) => (
              <Box key={`risk-${i}`}>
                <Typography variant="body2" fontWeight={500}>
                  {r.risk}
                </Typography>
                {r.why_it_matters && (
                  <Typography variant="body2" color="text.secondary">
                    {r.why_it_matters}
                  </Typography>
                )}
                {r.weak_vs_strong && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {r.weak_vs_strong}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {plan.length > 0 && (
        <Box>
          <SectionLabel>{t("InterviewReadinessOutput.thirtyDayPlan")}</SectionLabel>
          <Stack spacing={1.25} mt={0.75}>
            {plan.map((w, i) => (
              <Box key={`week-${i}`}>
                <Typography variant="body2" fontWeight={500}>
                  {t("InterviewReadinessOutput.weekPlan", { week: w.week, focus: w.focus })}
                </Typography>
                <BulletSection items={w.activities} />
                {w.deliverable && (
                  <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    {t("InterviewReadinessOutput.deliverableLabel", { deliverable: w.deliverable })}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {o.mock_interview_plan && (
        <Box>
          <SectionLabel>{t("InterviewReadinessOutput.mockInterviewPlan")}</SectionLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {o.mock_interview_plan}
          </Typography>
        </Box>
      )}

      {step.narrative_markdown && <LessonMarkdown>{step.narrative_markdown}</LessonMarkdown>}
    </Stack>
  );
}
